import moment from "moment";
import process from "process";
import { SlackNotificationType } from "../shared/enums";

export {};

/**
 * Formats a Slack mrkdwn message with a timestamped title, optional italic summary,
 * and an indented list of detail rows. Single source of truth for the standard Slack
 * message layout used by cron routes, server actions, and venue notifications.
 *
 * Note: callers are responsible for escaping any untrusted values with `escapeSlackText`
 * before passing them in — this helper does not escape.
 *
 * @param title - The bold headline (prefixed with the current timestamp).
 * @param message - Optional summary line, rendered in italics under the title.
 * @param details - Optional list of `{ title, message }` rows, rendered indented.
 * @returns The formatted Slack mrkdwn string.
 */
export function formatSlackMessage(
  title: string,
  message?: string,
  details?: { title: string; message: string }[]
): string {
  return (
    `*[${moment().format("MMMM Do, h:mm a")}] ${title}*\n${message ? "_" + message + "_\n" : ""}` +
    (details ?? []).map(({ title, message }) => `\t*${title}*: ${message}\n`).reduce((a, b) => a + b, "")
  );
}

/**
 * Helper function to call a fetch function on the client side.
 *
 * Will use cookies automatically to authenticate.
 *
 * @param url starts with /, does not require the environment variable
 * @returns fetch response
 */
// BATCH THE MESSAGES
let messageBatches: string[] = [];

export async function sendSlackMessageNoAuthForEmails(
  type: SlackNotificationType,
  title: string,
  message?: string,
  details?: { title: string; message: string }[],
  onlyMessage: boolean = false,
  flush: boolean = false
): Promise<void> {
  const webhookUrl = process.env[`SLACK_WEBHOOK_URL_${type}`];
  if (!webhookUrl) {
    console.error(`${type}_WEBHOOK_URL is not defined.`);
    throw new Error(`${type}_WEBHOOK_URL is not defined.`);
  }

  if (onlyMessage) {
    if (message) {
      messageBatches.push(message);
    }

    if (messageBatches.length >= 5 || flush) {
      const batchedMessage = messageBatches.join("\n");
      messageBatches = [];
      const res = await fetch(webhookUrl, {
        method: "POST",
        body: onlyMessage
          ? JSON.stringify({ text: batchedMessage })
          : JSON.stringify({
              text:
                `*[${moment().format("MMMM Do, h:mm a")}] ${title}*\n${message ? "_" + message + "_\n" : ""}` +
                (details ?? []).map(({ title, message }) => `\t*${title}*: ${message}\n`).reduce((a, b) => a + b, ""),
            }),
      });

      if (!res.ok) {
        debug("ERROR", res.statusText);
      }
    }
  } else {
    const res = await fetch(webhookUrl, {
      method: "POST",
      body: onlyMessage
        ? message
        : JSON.stringify({
            text:
              `*[${moment().format("MMMM Do, h:mm a")}] ${title}*\n${message ? "_" + message + "_\n" : ""}` +
              (details ?? []).map(({ title, message }) => `\t*${title}*: ${message}\n`).reduce((a, b) => a + b, ""),
          }),
    });

    if (!res.ok) {
      debug("ERROR", res.statusText);
    }
  }
}
// replaces console.log and pastes to slack for debugging purposes

export function debug(message?: any, ...optionalParams: any[]): void {
  console.log(message, optionalParams);

  let valueFormatted = "";
  for (const value of [message, ...optionalParams]) {
    try {
      const converted = JSON.stringify(value);

      valueFormatted += valueFormatted.length === 0 ? "" : " ";
      valueFormatted += converted;
    } catch (e) {}
  }

  sendSlackMessageNoAuthForEmails(SlackNotificationType.EMAIL, "", valueFormatted, undefined);
} /**
 * Retrieve or refresh credentials for Gmail API.
 * @returns {Promise<OAuth2Client>} The authorized OAuth2 client
 */

// Helper Functions

export async function sendSlackNotification(title: string, details: Record<string, any>, webhookUrl: string) {
  try {
    const message = `*${title}*\n${Object.entries(details)
      .map(([key, value]) => `*${key}:* ${value}`)
      .join("\n")}`;

    await fetch(webhookUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: message }),
    });
  } catch (error) {
    console.error("Failed to send Slack notification:", error);
  }
}
